import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import dayjs, { ManipulateType } from 'dayjs';

import { useBackgroundImageService } from 'hooks/use-background-image-service.hook';
import { getPrice } from 'helpers/payment';

import { TypographyVariants } from 'constants/typography-variants';
import { CurrencyLabelsByCodes } from 'constants/currency-labels';
import { PLANCODE_PRIMERIO_TYPE } from 'constants/trial-pay';

import { SkipTrialModalWindow } from 'pages/upgrade-plan/skip-trial-modal-window';
import { LoadingModalWindow } from 'pages/paywall/payment/loading-modal-window';

import { Typography } from 'components/typography';
import { NextButton } from 'components/next-button';

import { PLANS_FOR_UPGRADE_TYPE } from '../index';

import { useStyles } from './styles';

function getCurrencyLabelsByCode(code: string) {
    return CurrencyLabelsByCodes[code] || CurrencyLabelsByCodes.USD;
}

interface ContentProps {
    loading: boolean;
    showSkipTrialModalWindow: boolean;
    handleSkipTrialModalWindow: () => void;
    handleUpgradeTrialModalWindow: () => void;
    handleSkipUpgradeSubscription: () => void;
    handleUpgradeSubscription: () => void;
    plan: PLANCODE_PRIMERIO_TYPE;
    newPlan: PLANS_FOR_UPGRADE_TYPE;
}

export const Content: FC<ContentProps> = ({
    loading,
    showSkipTrialModalWindow,
    handleSkipTrialModalWindow,
    handleUpgradeTrialModalWindow,
    handleSkipUpgradeSubscription,
    handleUpgradeSubscription,
    plan,
    newPlan,

}) => {
    const { t } = useTranslation();

    const {
        pageWrapper,
        title,
        description,
        image,
        offerWrapper,
        calculationBlock,
        savingPercentWrapper,
        savingPercent,
        blue,
        totalWrapper,
        infoBlock,
        upgradePlanButton,
        continueWithTrialButton
    } = useStyles();

    const pageImages = useBackgroundImageService(['svg/upgrade-plan/content/commitment-graph.svg']);

    const currencyLabel = getCurrencyLabelsByCode(plan.currencyCode);

    const numberOfWeeks = dayjs().add(newPlan.chargePeriod, newPlan.chargeSubscriptionPeriodUnit as ManipulateType).diff(dayjs(), 'week');

    const originalWeekAmount = getPrice(plan.subscriptionAmount);

    const totalAmount = getPrice(newPlan.amount);

    const discountedWeekAmount = totalAmount / numberOfWeeks;

    const totalSaving = (originalWeekAmount * numberOfWeeks) - totalAmount;

    const savingPerWeek = +(originalWeekAmount - discountedWeekAmount).toFixed(2);

    const savingPerWeekInPercent = Math.round((savingPerWeek * 100) / originalWeekAmount);

    const planData = newPlan.key.split('-');

    const upgradePlanPeriod = +planData[planData.length - 2];
    const upgradePlanPeriodUnit = planData[planData.length - 1];

    let paidPeriod;
    let paidPeriodUnit;
    let freePeriod;
    let freePeriodUnit;

    if (upgradePlanPeriod === 1) {
        paidPeriod = 3;
        paidPeriodUnit = 'weeks';
        freePeriod = 1;
        freePeriodUnit = 'week';
    } else {
        if (upgradePlanPeriod === 3) {
            paidPeriod = 2;
            freePeriod = 1;
            freePeriodUnit = 'month';
        } else if (upgradePlanPeriod === 6) {
            paidPeriod = 4;
            freePeriod = 2;
            freePeriodUnit = 'months';
        } else {
            paidPeriod = 6;
            freePeriod = 6;
            freePeriodUnit = 'months';
        }
    }

    return (

        <div className={pageWrapper}>

            <LoadingModalWindow loading={loading} />

            <SkipTrialModalWindow showSkipTrialModalWindow={showSkipTrialModalWindow}
                                  upgradePlanMonthsCount={upgradePlanPeriod}
                                  handleSkipClick={handleSkipTrialModalWindow}
                                  handleUpgradeClick={handleUpgradeTrialModalWindow}
            />

            <Typography className={title} variant={TypographyVariants.h1} typographyNext>
                <Trans i18nKey='upgrade-plan-title' />
            </Typography>

            <Typography className={description} variant={TypographyVariants.h2}>
                <Trans i18nKey='upgrade-plan-description' components={[<strong></strong>]} />
            </Typography>

            <img className={image} src={pageImages[0]} alt='Commitment Graph' />

            <div className={offerWrapper}>
                <Typography variant={TypographyVariants.h1}>
                    <Trans i18nKey='upgrade-plan-commitment-offer-title' />
                </Typography>
                <Typography variant={TypographyVariants.h2} typographyNext>

                    {t('upgrade-plan-commitment-offer-subtitle', { upgradePlanPeriod,  upgradePlanPeriodUnit, paidPeriod, paidPeriodUnit, freePeriod, freePeriodUnit })}

                </Typography>

                <div className={calculationBlock}>
                    <Typography variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-week-plan-with-trial' />
                    </Typography>
                    <Typography variant={TypographyVariants.h3}>
                        {currencyLabel}{originalWeekAmount}
                    </Typography>
                </div>

                <div className={calculationBlock}>
                    <div className={savingPercentWrapper}>
                        <div className={savingPercent}>
                            <Typography variant={TypographyVariants.h3}>
                                <Trans i18nKey='upgrade-plan-saving-per-week-in-percent' values={{savingPerWeekInPercent}} />
                            </Typography>
                        </div>
                        <Typography className={blue} variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-skip-trial' />
                        </Typography>
                    </div>
                    <Typography className={blue} variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-saving-per-week' values={{currencyLabel, savingPerWeek}} />
                    </Typography>
                </div>

                <div className={calculationBlock}>
                    <Typography variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-total-savings' />
                    </Typography>
                    <Typography variant={TypographyVariants.h3}>
                        {currencyLabel}{totalSaving}
                    </Typography>
                </div>

                <div className={calculationBlock}>
                    <Typography variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-total' />
                    </Typography>
                    <div className={totalWrapper}>
                        <Typography variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-total-price-per-week' values={{ currencyLabel, discountedWeekAmount: discountedWeekAmount.toFixed(2) }} />
                        </Typography>
                        <Typography variant={TypographyVariants.h3}>
                            {currencyLabel}{originalWeekAmount}
                        </Typography>
                    </div>
                </div>

                <div className={calculationBlock}>
                    <Typography variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-billed-every' values={{upgradePlanMonths: upgradePlanPeriod, count: upgradePlanPeriod }} />
                    </Typography>
                    <Typography variant={TypographyVariants.h3}>
                        {currencyLabel}{totalAmount}
                    </Typography>
                </div>
            </div>

            <div className={infoBlock}>
                <ul>
                    <li>
                        <Typography variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-subscription-info' />
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-charge-info' values={{ savingPerWeek, currencyLabel, totalAmount, upgradePlanMonths: upgradePlanPeriod, count: upgradePlanPeriod }} />
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-cancel-info' />
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.h3}>
                            <Trans i18nKey='upgrade-plan-refund-info' />
                        </Typography>
                    </li>
                    {/*<li>*/}
                    {/*    <Typography variant={TypographyVariants.h3}>*/}
                    {/*        <Trans i18nKey='upgrade-plan-charge-currency-info' />*/}
                    {/*    </Typography>*/}
                    {/*</li>*/}
                </ul>
            </div>

            <NextButton className={upgradePlanButton} typographyText={t('upgrade-plan-skip-trial-button', { currencyLabel, savingPerWeek })} onClick={handleUpgradeSubscription} />
            <NextButton className={continueWithTrialButton} typographyText={t('upgrade-plan-continue-with-trial-button')} onClick={handleSkipUpgradeSubscription} />
        </div>
    );
};
